/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import { formatDate } from 'Tasking_Helpers/formatDate';
import { StyledTextSubHeader, StyledDataRowContainer } from './styles';
import getAuthClient from 'Tasking_Helpers/getAuthClient';

const AppointmentTracking = ({ userId }) => {
  const [newAppointment, setNewAppointment] = useState({
    appointmentDateUtc: '',
    appointmentReason: '',
    providerName: '',
  });
  const [appointments, setAppointments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAppointments = async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      const client = await getAuthClient();
      const response = await client.get(`${process.env.API_BASE_URL}/v1/appointment/${userId}`);
      setAppointments(response.data);
    } catch (error) {
      setError('Failed to fetch appointments. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAppointment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddAppointment = async () => {
    setIsSubmitting(true);
    setError(null);

    // First try/catch for adding the appointment
    try {
      const payload = {
        userId,
        appointmentDateUtc: newAppointment.appointmentDateUtc,
        appointmentReason: newAppointment.appointmentReason,
        providerName: newAppointment.providerName,
      };

      const client = await getAuthClient();
      await client.post(`${process.env.API_BASE_URL}/v1/appointment`, payload);

      // Clear form on successful POST
      setNewAppointment({
        appointmentDateUtc: '',
        appointmentReason: '',
        providerName: '',
      });

      // Appointment was added successfully
      // Now fetch appointments in a separate try/catch
      try {
        await fetchAppointments();
      } catch (fetchError) {
        // If fetching fails, show a different error that doesn't imply the appointment wasn't created
        setError(
          'Appointment added successfully, but unable to refresh the list. Please reload the page to see updates.',
        );
      }
    } catch (addError) {
      // This error means the appointment was not added
      setError('Failed to add appointment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3, mb: 3 }}>
      <Grid container>
        <Grid item xs={12}>
          <StyledDataRowContainer>
            <StyledTextSubHeader>Appointment Tracking</StyledTextSubHeader>
          </StyledDataRowContainer>
        </Grid>
      </Grid>
      <StyledDataRowContainer>
        {/* New Appointment Section */}
        <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              New Appointment
            </Typography>
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              name="appointmentDateUtc"
              label="Appt. Date"
              value={newAppointment.appointmentDateUtc}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              name="appointmentReason"
              label="Appt. Reason"
              value={newAppointment.appointmentReason}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={4}>
            <TextField
              fullWidth
              name="providerName"
              label="Provider/Agency Name"
              value={newAppointment.providerName}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={1}>
            <Button
              variant="contained"
              onClick={handleAddAppointment}
              disabled={
                isSubmitting ||
                !newAppointment.appointmentDateUtc ||
                !newAppointment.appointmentReason ||
                !newAppointment.providerName
              }
              sx={{
                height: '56px',
                width: '100%',
              }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </StyledDataRowContainer>
      <StyledDataRowContainer>
        {/* Appointment History Section */}
        <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 3 }}>
              Appointment History
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Appt. Date</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Appt. Reason</TableCell>
                    <TableCell>Provider/Agency Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map((appointment, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(appointment.appointmentDateUtc)}</TableCell>
                      <TableCell>{appointment.agentName}</TableCell>
                      <TableCell>{appointment.appointmentReason}</TableCell>
                      <TableCell>{appointment.providerName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </StyledDataRowContainer>
    </Paper>
  );
};

AppointmentTracking.propTypes = {
  userId: PropTypes.string.isRequired,
};

export default AppointmentTracking;
